import React, {useEffect, useState} from 'react'
import {
    Alert,
    Box,
    Button,
    Image,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl, FormattedNumber} from 'react-intl'
import {
    addedGiftCard,
    ApplyButton,
    checkBalanceButton,
    checkBalanceWraper,
    formErrorMessageStyle,
    giftCardBalanceStyle,
    giftCardHeading,
    inputFieldsWrapper,
    removeGiftCard
} from './giftCartPayment.style'
import {useForm} from 'react-hook-form'
import GiftCardField from './giftCardField'
import useGiftCardFields from './useGiftCardFields'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {pxToRem} from '../../../../utills/pxToRem'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useGiftCardBalance from '../../../../hooks/use-gift-card-balance'
import PropTypes from 'prop-types'

const GiftCardPayment = ({isOrderTotalZero = false}) => {
    const form = useForm()
    const intl = useIntl()

    const [giftCardError, setGiftCardError] = useState('')
    const [giftCardBalanceValue, setGiftCardBalanceValue] = useState('')
    const [enableBalanceCall, setEnableBalanceCall] = useState(false)

    const [cardNumberForBalance, setCardNumberForBalance] = useState('')
    const [pinForBalance, setPinForBalance] = useState('')

    const fields = useGiftCardFields({form, prefix: ''})
    const {data: basket} = useCurrentBasket()
    const {mutateAsync: addPaymentInstrumentToBasket} = useShopperBasketsMutation(
        'addPaymentInstrumentToBasket'
    )
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation(
        'removePaymentInstrumentFromBasket'
    )

    const {
        data: giftCardBalance,
        error,
        isError
    } = useGiftCardBalance(cardNumberForBalance, pinForBalance, enableBalanceCall)

    const handleCheckBalance = async () => {
        setEnableBalanceCall(false)
        const isValid = await form.trigger()
        if (isValid) {
            setEnableBalanceCall(true)

            setCardNumberForBalance(form.getValues('giftCardNumber'))
            setPinForBalance(form.getValues('pin'))
        } else {
            setEnableBalanceCall(false)
            setGiftCardError('')
        }
    }

    const hanldeRemoveGiftCard = async (instrumentId) => {
        try {
            await removePaymentInstrumentFromBasket({
                parameters: {
                    basketId: basket?.basketId,
                    paymentInstrumentId: instrumentId
                }
            })
        } catch (e) {
            setGiftCardError('Something went wrong, try removing gift card again!')
        }
    }

    const submitForm = async (data) => {
        setGiftCardError('')

        const giftCardBody = {
            paymentMethodId: 'GIFT_CERTIFICATE',
            giftCertificateCode: data?.giftCardNumber,
            c_giftCertificatePin: data?.pin
        }

        try {
            const giftCardResponse = await addPaymentInstrumentToBasket({
                parameters: {basketId: basket?.basketId},
                body: giftCardBody
            })
            if (giftCardResponse) {
                setGiftCardError('')
                form.setValue('giftCardNumber', '')
                form.setValue('pin', '')
            }
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            }

            if (errorBody?.statusCode === 'SYS-1021') {
                setGiftCardError(errorBody?.statusMessage)
                setGiftCardBalanceValue('')
            }
        }
    }

    useEffect(() => {
        if (giftCardBalance?.giftCertificate) {
            // this is used to display fetched balance of gift card
            setGiftCardBalanceValue(
                intl.formatMessage(
                    {
                        defaultMessage: 'Your current gift card balance is $ {amount}',
                        id: 'cart_checkout.gift_card.check_balance_value.label'
                    },
                    {
                        amount: giftCardBalance?.giftCertificate?.balance
                    }
                )
            )
            setGiftCardError('')

            // disable the flag so avoid unnecessary call of check balance
            setEnableBalanceCall(false)
        }

        if (isError) {
            // disable the flag so avoid unnecessary call of check balance
            setEnableBalanceCall(false)

            let errorBody
            const getError = async () => {
                if (error?.response instanceof Response) {
                    try {
                        errorBody = await error?.response?.clone()?.json()
                    } catch {
                        const text = await error?.response?.text()
                        try {
                            errorBody = JSON?.parse(text)
                        } catch {
                            errorBody = {message: text}
                        }
                    }
                }

                if (errorBody?.errorCode === 'SYS-1004') {
                    setGiftCardError(errorBody?.error)
                    setGiftCardBalanceValue('')
                } else {
                    setGiftCardError('Failed to fetch gift card balance, try again!')
                    setGiftCardBalanceValue('')
                }
            }
            getError()
        }
    }, [giftCardBalance?.giftCertificate, error, isError])

    useEffect(() => {
        setGiftCardBalanceValue('')
    }, [])

    return (
        <Box>
            <Text sx={giftCardHeading}>
                <FormattedMessage
                    defaultMessage="Pay with Gift Card"
                    id="checkout_payment.gift_card_heading.label"
                />
            </Text>

            <form
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form-register"
                noValidate
            >
                {/* error messages */}
                {form.formState.errors?.global && (
                    <Alert status="error" borderColor={'cav_red'}>
                        <AlertIcon color="cav_red" boxSize={4} />
                        <Text mt={0}>{form.formState.errors.global.message}</Text>
                    </Alert>
                )}

                <Box sx={inputFieldsWrapper}>
                    {/* card number */}
                    <GiftCardField {...fields?.giftCardNumber} />

                    {/* card pin */}
                    <GiftCardField
                        {...fields?.pin}
                        width={[pxToRem(82), pxToRem(82), pxToRem(101), pxToRem(101)]}
                    >
                        <Button
                            type="submit"
                            onClick={() => form.clearErrors('global')}
                            isLoading={form?.formState?.isSubmitting}
                            sx={ApplyButton}
                            isDisabled={isOrderTotalZero}
                        >
                            <FormattedMessage
                                defaultMessage="Apply"
                                id="checkout_payment.gift_card.button.apply"
                            />
                        </Button>
                        <Button
                            type="button"
                            variant={'link'}
                            onClick={handleCheckBalance}
                            sx={checkBalanceButton}
                        >
                            <FormattedMessage
                                defaultMessage="Check Balance"
                                id="checkout_payment.gift_card.button.check_balance"
                            />
                        </Button>
                    </GiftCardField>
                </Box>

                {(form?.formState?.errors?.giftCardNumber?.message ||
                    form?.formState?.errors?.pin?.message) && (
                    <Box sx={checkBalanceWraper}>
                        {/* gift card error message */}
                        <Text sx={formErrorMessageStyle}>
                            {form?.formState?.errors?.giftCardNumber?.message ||
                                form?.formState?.errors?.pin?.message}
                        </Text>
                    </Box>
                )}

                <Box sx={checkBalanceWraper}>
                    {/* gift card error message */}
                    {giftCardError && <Text sx={formErrorMessageStyle}>{giftCardError}</Text>}
                    {giftCardBalanceValue &&
                        !form?.formState?.errors?.giftCardNumber?.message &&
                        !form?.formState?.errors?.pin?.message && (
                            <Text sx={giftCardBalanceStyle}>{giftCardBalanceValue}</Text>
                        )}
                </Box>

                {basket?.paymentInstruments?.length && (
                    <Box>
                        {basket?.paymentInstruments?.map((instrument, index) => {
                            if (instrument?.paymentMethodId === 'GIFT_CERTIFICATE') {
                                return (
                                    <Box
                                        key={`${index}-gift-card`}
                                        lineHeight={'normal'}
                                        mt={pxToRem(10)}
                                    >
                                        <Text sx={addedGiftCard}>
                                            <Text as={'span'} marginRight="5px">
                                                <FormattedNumber
                                                    style="currency"
                                                    currency={basket?.currency}
                                                    value={instrument?.amount}
                                                />
                                            </Text>
                                            {intl.formatMessage(
                                                {
                                                    defaultMessage:
                                                        ' from gift card {card} applied',
                                                    id: 'cart_checkout.gift_card.added.label_new_dollar'
                                                },
                                                {
                                                    card: instrument?.c_cayanGiftCardMasked?.slice(
                                                        11
                                                    )
                                                }
                                            )}
                                        </Text>
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                hanldeRemoveGiftCard(
                                                    instrument?.paymentInstrumentId
                                                )
                                            }
                                            variant={'link'}
                                            sx={removeGiftCard}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Remove"
                                                id="checkout_payment.gift_card.button.remvoe_gift_card"
                                            />
                                        </Button>
                                    </Box>
                                )
                            }
                        })}
                    </Box>
                )}
            </form>
        </Box>
    )
}

GiftCardPayment.propTypes = {
    isOrderTotalZero: PropTypes.bool
}

export default GiftCardPayment
